import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { Button } from "@/components/ui/button";
import { FilePlus2, FileText, Clock, CheckCircle2, MapPin } from "lucide-react";
import { NeedHelp } from "@/components/NeedHelp";

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{value}</p>
      <p className="mt-0.5 text-sm text-slate-500">{label}</p>
    </div>
  );
}

export default function ClientDashboard() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ob, setOb] = useState(null);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
    api.get("/client/onboarding").then(({ data }) => setOb(data)).catch(() => {});
  }, []);

  const isActive = !ob || ob.client_status === "active";
  const canOrder = !ob || ["active", "pending_approval"].includes(ob.client_status);
  const active = orders.filter((o) => !["completed"].includes(o.status)).length;
  const completed = orders.filter((o) => o.status === "completed").length;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {ob && ob.client_status === "pending_approval" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4" data-testid="pending-approval-banner">
          <p className="text-sm text-indigo-800">Your account is submitted for ARPS review. Enter your service request now — you'll pay only after ARPS approves.</p>
          <Button data-testid="enter-request-btn" onClick={() => nav("/client/start")} className="bg-indigo-600 text-white hover:bg-indigo-700">Enter service request</Button>
        </div>
      )}
      {ob && !isActive && ob.client_status !== "pending_approval" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="onboarding-required-banner">
          <p className="text-sm text-amber-800">Client onboarding is incomplete. Complete onboarding and obtain ARPS approval before submitting a production order.</p>
          <Button data-testid="go-onboarding-btn" onClick={() => nav("/client/onboarding")} className="bg-amber-600 text-white hover:bg-amber-700">Complete onboarding</Button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Your service orders</h2>
          <p className="text-slate-500">Track every attempt and download proof of service.</p>
        </div>
        <Button data-testid="create-order-button" onClick={() => nav(canOrder ? "/client/start" : "/client/onboarding")} className="bg-emerald-600 text-white hover:bg-emerald-700">
          <FilePlus2 className="mr-1.5 h-4 w-4" /> Create an Order
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={FileText} label="Total orders" value={orders.length} tone="bg-slate-100 text-slate-700" />
        <StatCard icon={Clock} label="Active" value={active} tone="bg-sky-100 text-sky-700" />
        <StatCard icon={CheckCircle2} label="Completed" value={completed} tone="bg-emerald-100 text-emerald-700" />
        <StatCard icon={MapPin} label="Attempts logged" value={orders.reduce((s, o) => s + (o.attempts_count ?? 0), 0)} tone="bg-amber-100 text-amber-700" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="font-display text-lg font-bold text-slate-900">Recent orders</h3>
        </div>
        {loading ? (
          <p className="px-5 py-10 text-center text-slate-400">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <p className="text-slate-500">No service orders yet.</p>
            <Button data-testid="empty-create-order" onClick={() => nav("/client/start")} className="mt-4 bg-slate-900 text-white hover:bg-slate-800">Create your first order</Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {orders.map((o) => (
              <button
                key={o.id}
                data-testid={`order-row-${o.job_number}`}
                onClick={() => nav(`/client/orders/${o.id}`)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-arps text-sm font-semibold text-slate-900">#{o.job_number}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">{o.service_type}</span>
                  </div>
                  <p className="mt-0.5 truncate text-sm text-slate-600">{o.defendant} · {o.service_address?.city}, {o.service_address?.state}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <StatusBadge status={o.status} pulse={["in_progress", "assigned"].includes(o.status)} />
                  {o.status !== "completed" && <DeadlineBadge status={o.deadline_status} days={o.days_to_deadline} />}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <NeedHelp />
    </div>
  );
}
